import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";

const POLL_INTERVAL = 2500;
const MAX_TRIES = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, tries: 0 });

  // Payment confirmation is a transactional page — should NOT be indexed
  useEffect(() => {
    document.title = "Payment confirmation | Be Hire Ready";
    let el = document.querySelector('meta[name="robots"]');
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
    el.setAttribute("content", "noindex, nofollow");
  }, []);

  useEffect(() => {
    if (!sessionId) { setState({ status: "no_session", data: null, tries: 0 }); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, tries });
          return;
        }
        if (tries >= MAX_TRIES) {
          setState({ status: "timeout", data, tries });
          return;
        }
        setState({ status: "polling", data, tries });
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", data: null, tries });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0B132B] text-white grid place-items-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#3A506B]/30 blur-3xl" />
      <div className="relative w-full max-w-md rounded-3xl glass-dark p-8 text-center shadow-high">
        {state.status === "polling" && (
          <>
            <Loader2 size={44} className="mx-auto text-slate-300 animate-spin" />
            <div className="mt-4 font-display text-2xl">Confirming your payment…</div>
            <p className="mt-2 text-sm text-slate-400">This usually takes a couple of seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
            <div className="mt-4 font-display text-2xl">You're enrolled!</div>
            <p className="mt-2 text-sm text-slate-300">
              Thank you for enrolling in <b>{state.data?.program_name || "the Be Hire Ready program"}</b>. Our team will WhatsApp you within the next few hours to schedule your first session.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a href="https://wa.me/918824207033" target="_blank" rel="noreferrer"
                 className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white text-sm font-medium">
                <MessageCircle size={16} /> Continue on WhatsApp
              </a>
              <Link to="/" className="inline-flex items-center justify-center h-11 rounded-full ring-1 ring-white/10 text-sm">
                Back to homepage
              </Link>
            </div>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <div className="font-display text-2xl">Still processing…</div>
            <p className="mt-2 text-sm text-slate-400">
              Your payment is taking a little longer than expected. You'll receive a confirmation email once it's finalised.
            </p>
            <Link to="/" className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-full bg-white text-[#0B132B] text-sm font-medium">
              Back to homepage
            </Link>
          </>
        )}
        {state.status === "no_session" && (
          <>
            <div className="font-display text-2xl">Missing session</div>
            <p className="mt-2 text-sm text-slate-400">We couldn't find your payment session.</p>
            <Link to="/#programs" className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-full bg-white text-[#0B132B] text-sm font-medium">
              View Programs
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="font-display text-2xl">Something went wrong</div>
            <p className="mt-2 text-sm text-slate-400">Please refresh or WhatsApp us — we'll sort it out immediately.</p>
            <a href="https://wa.me/918824207033" className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-full bg-white text-[#0B132B] text-sm font-medium">
              Chat on WhatsApp
            </a>
          </>
        )}
      </div>
    </div>
  );
}
